/** Operations, remotely: spend by agent, model and provider; the local model; the daily review. */

import { Cpu, Persons, Pulse, Sparkles } from '@gravity-ui/icons';

import { ArtEmpty, ListGroup, ListRow, SectionHeading, Stat } from '../../components/ui.tsx';
import { useMoney } from '../../money.tsx';
import type { LocalModel, Usage, UsageTotals, Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

function Breakdown({ title, rows }: { title: string; rows?: Array<{ name: string; totals: UsageTotals }> }) {
  const { format } = useMoney();
  if (!rows?.length) return null;
  return (
    <ListGroup title={title} count={rows.length}>
      {rows.slice(0, 8).map((r) => (
        <ListRow key={r.name} title={r.name} value={<span className="tabular-nums">{`${format(r.totals.costUsd)} · ${r.totals.requests} calls`}</span>} />
      ))}
    </ListGroup>
  );
}

export function OperationsView({ w }: { w: Workbench }) {
  const { format } = useMoney();
  const usage = w.useView<Usage>('usage.history', { days: 7 }, [], 60_000);
  const local = w.useView<LocalModel>('localModel.status', undefined, [], 30_000);
  const u = usage.data;
  const a = w.activity.data;
  const any = !!(u?.byAgent?.length || u?.byModel?.length || u?.byProvider?.length || u?.byRuntime?.length);
  return (
    <RemoteView>
      <div className="art-stats">
        <Stat tone="green" icon={<Pulse />} label="Spend, 7 days" value={u ? format(u.totals.costUsd) : undefined} detail={u ? `${u.totals.requests} calls${u.totals.partialCost ? ' · some costs unknown' : ''}` : ''} />
        <Stat tone="violet" icon={<Sparkles />} label="Tokens, 7 days" value={u ? Math.round((u.totals.promptTokens + u.totals.completionTokens) / 1000) : undefined} detail="thousand, prompt and completion" />
        <Stat tone="teal" icon={<Persons />} label="Agents" value={a?.agents.length} detail={a ? `${a.working} working` : ''} />
        <Stat tone="blue" icon={<Cpu />} label="Local model" value={local.data ? (local.data.available ? 'On' : 'Off') : undefined} detail={local.data?.model ?? local.data?.reason ?? ''} />
      </div>
      <SectionHeading title="Spend" description="Where the last seven days went, by agent, model, provider and runtime." />
      {any ? (
        <div className="@container">
          <div className="grid gap-4 @[44rem]:grid-cols-2">
            <Breakdown title="By agent" rows={u?.byAgent} />
            <Breakdown title="By model" rows={u?.byModel} />
            <Breakdown title="By provider" rows={u?.byProvider} />
            <Breakdown title="By runtime" rows={u?.byRuntime} />
          </div>
        </div>
      ) : (
        <ArtEmpty compact tone="blue" icon={<Pulse />} title="No spend this week">
          {w.connected ? 'Calls the agents make show here by agent, model, provider and runtime.' : 'The workbench is not connected.'}
        </ArtEmpty>
      )}
    </RemoteView>
  );
}
